import { ButtonHTMLAttributes, Component, HTMLAttributes } from 'react';

import getStyles from './Button.style';

interface Props {
  block?: boolean;
  className?: string;
  color?:
    | 'danger'
    | 'default'
    | 'pink'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'action';
  disabled: ButtonHTMLAttributes<HTMLButtonElement>['disabled'];
  hasBorder?: boolean;
  id?: string;
  inverse?: boolean;
  link?: string;
  noBg?: boolean;
  noHover?: boolean;
  onClick?: HTMLAttributes<HTMLAnchorElement | HTMLButtonElement>['onClick'];
  rounded?: boolean;
  style?: HTMLAttributes<HTMLAnchorElement | HTMLButtonElement>['style'];
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
}

class Button extends Component<Props> {
  buttonRef: HTMLAnchorElement | HTMLButtonElement | null;

  static defaultProps = {
    block: false,
    className: '',
    color: 'default',
    disabled: false,
    hasBorder: false,
    inverse: false,
    link: '',
    noBg: false,
    noHover: false,
    rounded: true,
    type: 'button',
  };

  constructor(props: Props) {
    super(props);
    this.buttonRef = null;
  }

  focus() {
    this.buttonRef?.focus();
  }

  render() {
    const {
      block,
      children,
      className,
      color = 'default',
      disabled = false,
      hasBorder,
      inverse = false,
      link,
      noBg,
      noHover,
      rounded,
      type = 'button',
      ...restProps
    } = this.props;

    const styles = getStyles(color, inverse);

    const backgroundColor = noBg ? 'transparent' : styles.backgroundColor;
    const classNames = `zbtn ${className ?? ''}`;

    return (
      <>
        {link ? (
          /* eslint-disable react/jsx-props-no-spreading */
          <a
            {...restProps}
            className={classNames}
            href={link}
            ref={(ref) => {
              this.buttonRef = ref;
            }}
          >
            {children}
          </a>
        ) : (
          // eslint-disable-next-line react/button-has-type
          <button
            {...restProps}
            className={classNames}
            disabled={disabled}
            ref={(ref) => {
              this.buttonRef = ref;
            }}
            type={type}
          >
            {children}
          </button>
          /* eslint-enable react/jsx-props-no-spreading */
        )}

        <style jsx>
          {`
            .zbtn {
              -webkit-font-smoothing: antialiased;
              appearance: none;
              border-radius: ${rounded ? '4px' : 0};
              box-shadow: none;
              cursor: pointer;
              display: inline-block;
              font-weight: 600;
              line-height: 1rem;
              outline: none;
              padding: 0.9375rem 1rem;
              text-align: center;
              text-decoration: none;
              text-transform: uppercase;
              user-select: none;
              vertical-align: middle;
              white-space: nowrap;
              background-color: ${backgroundColor};
              border: 1px solid ${hasBorder ? '#e7e7e7' : 'transparent'};
              color: ${styles.color};
              width: ${block ? '100%' : 'auto'};
              min-width: 145px;
            }

            .rounded {
              border-radius: 46px !important;
              box-shadow: 2px 3px 4px 3px rgba(47, 47, 47, 0.25);
            }

            .zbtn:hover,
            .zbtn:active {
              color: ${noHover ? styles.color : styles.colorHover};
              background-color: ${noHover ? backgroundColor : styles.backgroundColorHover};
            }

            .zbtn:active {
              opacity: 0.5;
            }

            .zbtn[disabled] {
              opacity: 0.6;
              cursor: not-allowed;
            }
            .zbtn[disabled]:hover {
              background-color: ${backgroundColor};
            }
          `}
        </style>
      </>
    );
  }
}

export default Button;
